import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { fetchPwaAuthUser, logoutPwaAccount, logoutTelegram, type PwaAuthUser, type TelegramUser } from '../../utils/telegramAuth';
import { fetchSentryFeeSwapSummary, type SentryFeeSwapSummary } from '../../utils/sentryBotApi';

const SENTRY_LAUNCH_MARKET_BUY = 19465720.490301692;

const formatTokenAmount = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return '--';
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: amount < 1 ? 4 : 2,
    maximumFractionDigits: amount < 1 ? 6 : 2
  });
};

const formatUsdAmount = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return '--';
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const SentryDesktopHeader: React.FC = () => {
  const navigate = useNavigate();
  const [authUser, setAuthUser] = useState<PwaAuthUser | TelegramUser | null>(null);
  const [feeSwapSummary, setFeeSwapSummary] = useState<SentryFeeSwapSummary | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadUser = async () => {
      const user = await fetchPwaAuthUser();
      if (!cancelled) {
        setAuthUser(user);
      }
    };
    loadUser();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const fetchSummary = async () => {
      try {
        const summary = await fetchSentryFeeSwapSummary();
        if (!cancelled && summary) {
          setFeeSwapSummary(summary);
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('[SentryDesktopHeader] Failed to fetch fee swap summary:', error);
        }
      } finally {
        if (cancelled) return;
        const shouldPollFast = feeSwapSummary?.truncated || false;
        const nextDelayMs = shouldPollFast ? 60 * 1000 : 30 * 60 * 1000;
        timeoutId = setTimeout(fetchSummary, nextDelayMs);
      }
    };

    fetchSummary();

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [feeSwapSummary?.truncated]);

  const handleStatusClick = () => {
    if (authUser) {
      navigate('/sentry-desktop/wallet');
    } else {
      navigate('/sentry-desktop');
    }
  };

  const handleLogout = async () => {
    try {
      if (authUser && 'authType' in authUser && authUser.authType === 'account') {
        await logoutPwaAccount();
      } else {
        await logoutTelegram();
      }
    } finally {
      setAuthUser(null);
      navigate('/sentry-desktop');
    }
  };

  const sentryBuyBacks = feeSwapSummary
    ? Math.max(0, feeSwapSummary.totalSentryBought - SENTRY_LAUNCH_MARKET_BUY)
    : null;
  const sentryBuyBacksUsd = sentryBuyBacks !== null && feeSwapSummary
    ? sentryBuyBacks * feeSwapSummary.sentryPriceUsd
    : null;
  const headerFeeStats = [
    {
      label: 'Sentry Bot Trades',
      value: feeSwapSummary ? feeSwapSummary.totalBuys.toLocaleString('en-US') : '--',
      tone: 'green' as const
    },
    {
      label: '$SENTRY Buy Backs',
      value: formatTokenAmount(sentryBuyBacks),
      tone: 'gold' as const
    },
    {
      label: 'USD Value',
      value: formatUsdAmount(sentryBuyBacksUsd),
      tone: 'green' as const
    }
  ];

  return (
    <header className="sentry-desktop-header">
      <div className="sentry-desktop-header-inner">
        <div className="sentry-desktop-left">
          <button
            type="button"
            className="sentry-desktop-logo"
            onClick={() => navigate('/sentry-desktop/dashboard')}
          >
            <img src="/Sentry-Logo.svg" alt="Sentry" />
          </button>
          <div className="sentry-desktop-stats">
            {headerFeeStats.map((stat) => (
              <div key={stat.label} className="sentry-desktop-stat">
                <span className="sentry-desktop-stat-label">{stat.label}</span>
                <span className={`sentry-desktop-stat-value tone-${stat.tone}`}>{stat.value}</span>
              </div>
            ))}
          </div>
        </div>
        <nav className="sentry-desktop-nav">
          <NavLink
            to="/sentry-desktop/tokens"
            className={({ isActive }) =>
              `sentry-desktop-nav-link${isActive ? ' active' : ''}`
            }
          >
            Tokens
          </NavLink>
          <NavLink
            to="/sentry-desktop/dashboard"
            className={({ isActive }) =>
              `sentry-desktop-nav-link${isActive ? ' active' : ''}`
            }
          >
            Ecosystem
          </NavLink>
          <NavLink
            to="/sentry-desktop/wallet"
            className={({ isActive }) =>
              `sentry-desktop-nav-link${isActive ? ' active' : ''}`
            }
          >
            Wallet
          </NavLink>
          <NavLink
            to="/sentry-desktop/docs"
            className={({ isActive }) =>
              `sentry-desktop-nav-link${isActive ? ' active' : ''}`
            }
          >
            Docs
          </NavLink>
          <NavLink
            to="/sentry-desktop/create"
            className={({ isActive }) =>
              `sentry-desktop-nav-link${isActive ? ' active' : ''}`
            }
          >
            Create
          </NavLink>
        </nav>
        <div className="sentry-desktop-actions">
          {authUser && (
            <button
              type="button"
              className="sentry-desktop-logout"
              onClick={handleLogout}
              title="Sign out"
            >
              Logout
            </button>
          )}
          <button
            type="button"
            className="sentry-desktop-status"
            onClick={handleStatusClick}
            title={authUser ? 'View wallet' : 'Sign in'}
          >
            <span className="sentry-desktop-status-icon">
              {authUser ? '●' : '○'}
            </span>
            <span className="sentry-desktop-status-text">
              {authUser?.username ? `@${authUser.username}` : authUser ? 'Connected' : 'Sign In'}
            </span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default SentryDesktopHeader;
